// Custom authentication hooks for Heapster ID
// Provides convenient interfaces for authentication operations
// Built on top of AuthContext for consistent state management

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthContext } from '../contexts/AuthContext';
import type { SignInData, SignUpData } from '../lib/schemas';
import { handleFirebaseError } from '../lib/firebase-errors';

// Re-export the main auth hook for convenience
export { useAuthContext as useAuth };

// Sign In Hook with React Query integration
export function useSignIn() {
  const { signIn } = useAuthContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ email, password }: SignInData) => {
      await signIn(email, password);
    },
    onSuccess: () => {
      // Invalidate and refetch any user-related queries
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
    onError: error => {
      const friendlyError = handleFirebaseError(error);
      console.error('Sign in failed:', friendlyError);
    },
  });
}

// Sign Up Hook with React Query integration
export function useSignUp() {
  const { signUp } = useAuthContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      email,
      password,
      display_name,
      access_code,
    }: SignUpData) => {
      await signUp(email, password, display_name, access_code);
    },
    onSuccess: () => {
      // Invalidate and refetch any user-related queries
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
    onError: error => {
      const friendlyError = handleFirebaseError(error);
      console.error('Sign up failed:', friendlyError);
    },
  });
}

// Sign Out Hook with React Query integration
export function useSignOut() {
  const { signOut } = useAuthContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await signOut();
    },
    onSuccess: () => {
      // Clear all cached data on sign out
      queryClient.clear();
    },
    onError: error => {
      console.error('Sign out failed:', error);
    },
  });
}

// Email Verification Hook
export function useEmailVerification() {
  const { sendVerificationEmail } = useAuthContext();

  return useMutation({
    mutationFn: async () => {
      await sendVerificationEmail();
    },
    onError: error => {
      console.error('Email verification failed:', error);
    },
  });
}

// User Profile Refresh Hook
export function useRefreshProfile() {
  const { refreshUserProfile } = useAuthContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await refreshUserProfile();
    },
    onSuccess: () => {
      // Invalidate user-related queries
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
    onError: error => {
      console.error('Profile refresh failed:', error);
    },
  });
}

// Utility hooks for common authentication checks

// Check if user is authenticated
export function useIsAuthenticated(): boolean {
  const { user, isLoading } = useAuthContext();
  return !isLoading && !!user;
}

// Check if user has completed profile setup (PRD Section 5.2)
export function useIsProfileComplete(): boolean {
  const { user, profileCompleted } = useAuthContext();
  return !!user && profileCompleted;
}

// Check if user's email is verified
export function useIsEmailVerified(): boolean {
  const { isEmailVerified } = useAuthContext();
  return isEmailVerified;
}

// Get current user data (null if not authenticated)
export function useCurrentUser() {
  const { user, isLoading } = useAuthContext();
  return { user, isLoading };
}

// Authentication guard hook - throws error if not authenticated
export function useRequireAuth() {
  const { user, isLoading } = useAuthContext();

  if (!isLoading && !user) {
    throw new Error('Authentication required');
  }

  return { user, isLoading };
}
